import { Link } from "react-router-dom";
import { KineticHeading } from "../components/site/KineticHeading";
import { Reveal } from "../components/site/Reveal";
import { useContent } from "../context/ContentContext";

export default function PrivacyPolicy() {
  const { site } = useContent();
  const c = site.contact || {};
  const email = c.email || "hello@korio.eu";
  const company = c.company || "KORIO SRL";
  const addr = (c.address || ["Tollaan 75", "1932 Sint-Stevens-Woluwe", "Belgium"]).join(", ");

  const sections = [
    {
      n: "01", title: "Who are we?",
      body: [
        "Organizing an event is always an important challenge for agencies. They spend all their energy in thousands of details in order to have happy visitors and organizers.",
        "KORIO's goal is to improve organizers' efficiency and to create a unique customer experience by using cutting-edge innovation.",
        "Our story starts as a Software Developers' Company in early 2000. The first major event we worked for was in 2002 on a huge international medical congress and since, KORIO never stopped working in this sector.",
        "The expansion of KORIO occurred by organic growth, but also with some major acquisitions: Cerix (Belgian leading company in short-term IT rental), AIM (Voting Systems), Whos-in (ticketing and access control), Getyoo (interactive services for visitors on public and professional fairs), Bebotics (photobooths and custom event experiences) and Eventattitude (Brand Activation interactive & personalised experiences).",
        "Today, KORIO's services consist of 3 main axes: Event Technology, Brand Activation and IT/AV rental.",
        "We take your privacy seriously and are committed to comply with data protection laws, and especially the General Data Protection Regulation (GDPR).",
        `KORIO is a company existing and operating under Belgian law, with statutory seat located at ${addr} and registered at the Crossroads Bank for Enterprises under company number 0478.889.493.`,
        "We use our best efforts to bring the data processing activities of KORIO into compliance with applicable data protection legislation, including Regulation (EU) 2016/679 (GDPR) and the Belgian Data Protection Act, each as applicable and amended from time to time.",
      ],
    },
    {
      n: "02", title: "Which data?",
      body: [
        "Considered as confidential information within the meaning of this Regulation: all employer information that has not been made public. Confidential information that has been made public illegally must also be considered as confidential information.",
        "We most commonly — but not always — process the following categories of confidential information about you:",
      ],
      list: [
        "Your personal data (last name, first name, address, city, country)",
        "Your professional data (company, job title)",
        "Your email address",
        "The picture representing you and the other persons on the picture",
        "On an aggregated basis, limited socio-demographic data: gender, age (or range), language",
        "The answers that were asked by our client and that you were invited to fill in",
      ],
    },
    {
      n: "03", title: "Employees' commitment",
      body: [
        "This regulation contains directives to be respected by all KORIO's employees in the exercise of their functions. Employees come into contact with confidential information in the performance of their duties and must maintain it at any time under the seal of secrecy.",
        "Employees commit not to share confidential information voluntarily or involuntarily with unauthorised persons, including third parties, and to never use it at the expense of the employer or for any other purpose than those of their mission.",
      ],
    },
    {
      n: "04", title: "Your rights",
      body: ["Subject to applicable data protection laws, you have the rights to access, rectify and erase your personal data, the rights to object to or limit the processing of your personal data and the right to data portability, meaning that:"],
      list: [
        "You have the right to be informed about how we use your personal data and how to exercise your rights.",
        "You have the right to obtain access to your personal data and to check we are using it lawfully.",
        "You have the right to request the deletion of your personal data where there is no compelling reason for us to keep using it.",
        "You are entitled to have your personal data rectified if it is inaccurate or incomplete.",
        "You have the right to 'block' or suppress further use of your personal data.",
        "You have the right to obtain and reuse your personal data for your own purposes.",
        "You have the right to lodge a complaint with your national data protection authority.",
        "If you have given your consent, you have the right to withdraw it at any time.",
      ],
      after: `Please forward any request regarding your rights as a data subject to us by email at ${email}. We will comply as soon as reasonably practicable and within the timeframes set forth by applicable data protection law. We may ask you for proof of identity if we have a doubt.`,
    },
    {
      n: "05", title: "Protection of confidential information / Security measures",
      body: [
        "Legally, if we received your personal data with your consent and with a legitimate interest to collect and disclose it to our client, we are within our rights.",
        "We will ask for your free, prior and informed consent if our clients want to use your personal data for direct marketing purposes or want to publish your picture online or use it for internal or external corporate communications.",
        "We do not subject you to decisions based exclusively on automated processing. We do not collect personal data about you from third parties. We analyse the risks and reassess them specifically for each data collected, and data collection is done according to the rules of the GDPR.",
      ],
    },
    {
      n: "06", title: "Transport of confidential information",
      body: ["In case of transport of confidential information (e.g. on a USB key or a laptop outside of the business premises), employees take into account the following guidelines:"],
      list: [
        "Employees limit the transport of confidential information to what is strictly necessary for the performance of their work.",
        "Employees remain permanently aware of the risk of data loss in this type of situation.",
        "Employees do everything in their power to prevent the theft or loss of data.",
        "Employees never leave their laptop unattended outside of the workplace and report any theft or hack to their superior within six (6) hours.",
        "Employees only use secure networks and never an open, unsecured WIFI network.",
      ],
    },
    {
      n: "07", title: "Where is my confidential information stored?",
      body: [
        "All data are stored in a SQL database, secured by an encrypted login and password. The data center is located in Europe and duplicated in Belgium for security reasons.",
        "The data will be provided to our client, if allowed by GDPR, through an encrypted file. The data at rest will be deleted by KORIO at the end of the mission.",
        "The servers are physically located with our GDPR-compliant subcontractors located in Belgium and the EU. Inside KORIO, the data only carries through the officers in charge of the project; each computer is protected by a personal password and the building is protected by an alarm system.",
      ],
    },
    {
      n: "08", title: "How long do we retain your personal data?",
      body: ["We retain your personal data as long as necessary to achieve the purposes for which we process it. KORIO, our clients and sub-processors do not keep data longer than necessary and won't pass it on to any third party without explicit authorisation. We use the following criteria to determine retention periods:"],
      list: [
        "The time elapsed since the event",
        "The sensitivity of the personal data",
        "Security reasons",
        "Any current or potential dispute or litigation",
        "Any legal or regulatory obligation to retain or delete personal data",
      ],
    },
    {
      n: "09", title: "How can I stop receiving marketing e-mails?",
      body: [
        "If you would like to stop receiving e-mails from our clients, you can opt out at any time by unsubscribing from their mailing list or by sending them an email.",
        "You have the right to object at any time to the processing of your personal data for direct marketing purposes, and to lodge a complaint at the competent data protection authority:",
        "Belgian Data Protection Authority — Rue de la Presse 35, 1000 Bruxelles — commission@privacycommission.be — +32 (0)2 274 48 00",
      ],
    },
    {
      n: "10", title: "I have a question or a problem. Who can I contact?",
      body: [`If you have any question or complaint about the processing of your personal data, please contact us by email at ${email} or by post at ${company}, ${addr}.`],
    },
  ];

  return (
    <section className="mx-auto max-w-[900px] px-6 pb-28 pt-40 md:px-10 md:pt-48" data-testid="privacy-page">
      <div className="mb-6 text-xs font-bold uppercase tracking-[0.25em] text-volt">Legal · GDPR</div>
      <KineticHeading lines={["Privacy Policy"]}
        className="font-display text-5xl font-semibold uppercase leading-[0.95] tracking-tighter sm:text-6xl lg:text-7xl" />
      <p className="mt-8 text-lg leading-relaxed text-white/60">
        This is the “regulation on the Protection of privacy” of {company}. We take your privacy seriously and
        are committed to comply with data protection laws, especially the General Data Protection Regulation (GDPR).
      </p>

      <div className="mt-16 space-y-14">
        {sections.map((s) => (
          <Reveal key={s.n}>
            <div className="border-t border-white/10 pt-8">
              <div className="flex gap-6">
                <span className="font-mono text-sm text-volt">{s.n}</span>
                <div>
                  <h2 className="font-display text-2xl font-medium tracking-tight sm:text-3xl">{s.title}</h2>
                  <div className="mt-5 space-y-4">
                    {s.body.map((p, i) => <p key={i} className="leading-relaxed text-white/60">{p}</p>)}
                    {s.list && (
                      <ul className="mt-2 space-y-2">
                        {s.list.map((li, i) => (
                          <li key={i} className="flex gap-3 text-white/60">
                            <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-volt" />{li}
                          </li>
                        ))}
                      </ul>
                    )}
                    {s.after && <p className="leading-relaxed text-white/60">{s.after}</p>}
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        ))}
      </div>

      <div className="mt-16 rounded-2xl border border-white/10 bg-korio-surface p-8">
        <h3 className="font-display text-xl font-medium tracking-tight">Questions about your data?</h3>
        <p className="mt-3 text-white/60">Email us at <a href={`mailto:${email}`} className="text-gradient font-semibold">{email}</a> or write to {company}, {addr}.</p>
        <div className="mt-6 flex flex-wrap gap-4">
          <Link to="/contact" className="brand-gradient rounded-full px-6 py-3 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5">Contact us</Link>
          <Link to="/" className="rounded-full border border-white/20 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/5">Back to home</Link>
        </div>
      </div>
    </section>
  );
}
